import React from 'react'
import { cn } from '@/lib/utils/cn'
import { Tooltip } from '@/design-system/Tooltip'
import { CheckCircle2 } from 'lucide-react'

interface VerifiedBadgeProps {
  /** When true, renders the badge. When false/undefined, renders nothing. */
  verified?: boolean
  /** 'icon' is a tight single-line chip suitable for inline use beside a name;
   *  'pill' is the chunkier badge used in profile headers. */
  size?: 'icon' | 'pill'
  /** Override the displayed label (defaults to "BankID-verifierad"). */
  label?: string
  className?: string
}

/**
 * VerifiedBadge — single visual signal that a customer or provider has
 * verified their identity via BankID. Used on quote cards, request rows,
 * and profile headers to surface the trust signal both directions.
 *
 * When `verified` is false, the component renders nothing — callers can
 * pass `verified={profile.bankid_verified}` without conditional guards.
 */
export function VerifiedBadge({
  verified,
  size = 'pill',
  label = 'BankID-verifierad',
  className,
}: VerifiedBadgeProps) {
  if (!verified) return null

  if (size === 'icon') {
    return (
      <Tooltip content={label}>
        <span
          className={cn(
            'inline-flex h-4 w-4 items-center justify-center rounded-full text-[#2563eb]',
            className,
          )}
          aria-label={label}
          data-testid="verified-badge"
        >
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
        </span>
      </Tooltip>
    )
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200',
        className,
      )}
      data-testid="verified-badge"
    >
      <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  )
}

export default VerifiedBadge
